/**
 * @fileoverview Asset utility functions for type checking and discrimination.
 *
 * Provides type-safe utilities for working with the Asset type discriminator,
 * with backwards compatibility for legacy assets that don't have assetType set.
 */

import type { Asset, AssetType, Job } from '../types'
import { useStore, addAssetFromJob } from '../store/yjsStore'
import { transformProxyUri } from './proxyResolution'
import { getStorageUrl } from './api'
import { queueFullFileVideoPreload } from './videoPreloadCoordinator'
import { useSubmissionProcessorStore } from '../store/submissionProcessorStore'
import { yTransact } from './multiplayer/bootstrap'
import { SYSTEM_ORIGIN } from './constants'
import { getAssetMediaKind, type AssetMediaKindInput } from './assetMediaKind'

export { getAssetMediaKind, type AssetMediaKindInput } from './assetMediaKind'

/**
 * Returns the display prompt for an asset.
 * Set screenshots store lens metadata as prompt (e.g. "Shot with 50mm") which is not a real prompt.
 * This utility returns '' for set-screenshot assets so the fake prompt is never shown.
 */
export function getAssetPrompt(asset: Pick<Asset, 'prompt' | 'operation'> | null | undefined): string {
  if (!asset) return ''
  if (asset.operation === 'set-screenshot') return ''
  return asset.prompt || ''
}

export function getAssetType(asset: Asset): AssetType {
  return getAssetMediaKind(asset)
}

/**
 * Type guard for audio assets.
 * If assetType is explicitly set, trust it as the source of truth.
 * Falls back to mimeType detection for legacy assets without assetType.
 *
 * @param asset - The asset to check
 * @returns True if the asset is an audio asset
 */
export function isAudioAsset(asset: Asset): asset is Asset & { assetType: 'audio' } {
  return getAssetMediaKind(asset) === 'audio'
}

/**
 * Type guard for video assets.
 * If assetType is explicitly set, trust it as the source of truth.
 * Falls back to mimeType detection for legacy assets without assetType.
 * Legacy assets without assetType default to video.
 *
 * @param asset - The asset to check
 * @returns True if the asset is a video asset
 */
export function isVideoAsset(asset: Asset): asset is Asset & { assetType: 'video' } {
  return getAssetMediaKind(asset) === 'video'
}

/**
 * Type guard for image assets.
 * If assetType is explicitly set, trust it as the source of truth.
 * Falls back to mimeType detection for legacy assets without assetType.
 *
 * @param asset - The asset to check
 * @returns True if the asset is an image asset
 */
export function isImageAsset(asset: Asset): asset is Asset & { assetType: 'image' } {
  return getAssetMediaKind(asset) === 'image'
}

/**
 * Type guard for set assets (3D sets).
 * Sets are a distinct asset type representing 3D environments.
 *
 * @param asset - The asset to check
 * @returns True if the asset is a set asset
 */
export function isSetAsset(asset: AssetMediaKindInput): boolean {
  return getAssetMediaKind(asset) === 'set'
}

/**
 * Camera-control prep nodes are video assets semantically, but on the canvas
 * they share Step Into Set's node and viewer treatment. Saved motion drafts
 * opt out so they render as normal video-sized draft/render nodes.
 */
export function isCameraMotionControlAsset(asset: {
  assetType?: AssetType | string | null
  mimeType?: string | null
  cameraMotion?: {
    nodeType?: 'source' | 'control' | 'draft'
    guideVideoUrl?: string
    userVisibleGuideVideoUrl?: string
    outputVideoUrl?: string
  } | null
  operation?: string | null
}): boolean {
  if (asset.cameraMotion?.nodeType === 'source') return false
  if (asset.cameraMotion?.nodeType === 'control') return true
  if (asset.cameraMotion?.nodeType === 'draft') return false
  if (
    asset.cameraMotion?.guideVideoUrl ||
    asset.cameraMotion?.userVisibleGuideVideoUrl ||
    asset.cameraMotion?.outputVideoUrl
  )
    return false
  if (asset.operation === 'camera-motion') return true
  return asset.cameraMotion != null && getAssetMediaKind(asset) === 'video'
}

/**
 * Deep links with `open=motion` should open fresh source/control nodes in the
 * camera-motion editor. Draft/rendered motion videos stay in their normal
 * detail-panel flow.
 */
export function canOpenCameraMotionEditorFromAsset(asset: {
  assetType?: AssetType | string | null
  mimeType?: string | null
  cameraMotion?: {
    nodeType?: 'source' | 'control' | 'draft'
    guideVideoUrl?: string
    userVisibleGuideVideoUrl?: string
    outputVideoUrl?: string
  } | null
  operation?: string | null
}): boolean {
  if (asset.cameraMotion?.nodeType === 'source' || asset.cameraMotion?.nodeType === 'control') return true
  if (asset.cameraMotion?.nodeType === 'draft') return false
  return isCameraMotionControlAsset(asset)
}

export function shouldSuppressCameraMotionPreview(asset: {
  cameraMotion?: {
    nodeType?: 'source' | 'control' | 'draft'
    status?: string
    guideVideoUrl?: string
    outputVideoUrl?: string
  } | null
  operation?: string | null
}): boolean {
  const motion = asset.cameraMotion
  if (!motion) return false
  if (motion.nodeType === 'source') return false
  if (motion.nodeType === 'control') return true
  if (motion.nodeType === 'draft') return motion.status !== 'rendered'
  return isCameraMotionControlAsset(asset)
}

type CameraMotionGuideReferenceAsset = {
  cameraMotion?: {
    nodeType?: 'source' | 'control' | 'draft'
    status?: string | null
    guideVideoUrl?: string | null
    userVisibleGuideVideoUrl?: string | null
    outputVideoUrl?: string | null
  } | null
  operation?: string | null
}

type CameraMotionGuideVideoReference = {
  videoAssetId?: string | null
  videoUrl?: string | null
}

function safeStorageUrl(value: string): string {
  try {
    return getStorageUrl(value)
  } catch {
    return value
  }
}

function comparableMediaUrlKeys(value: string | null | undefined): Set<string> {
  const keys = new Set<string>()
  if (!value) return keys

  for (const candidate of [value, safeStorageUrl(value)]) {
    const withoutQuery = candidate.split(/[?#]/, 1)[0]?.trim()
    if (!withoutQuery) continue

    keys.add(withoutQuery)

    try {
      const parsed = new URL(withoutQuery)
      const pathKey = parsed.pathname.replace(/^\/storage\//, '').replace(/^\/+/, '')
      if (pathKey) keys.add(pathKey)
    } catch {
      const pathKey = withoutQuery.replace(/^\/storage\//, '').replace(/^\/+/, '')
      if (pathKey) keys.add(pathKey)
    }
  }

  return keys
}

function mediaUrlsMatch(candidate: string | null | undefined, protectedUrl: string | null | undefined): boolean {
  const candidateKeys = comparableMediaUrlKeys(candidate)
  if (candidateKeys.size === 0) return false

  for (const protectedKey of comparableMediaUrlKeys(protectedUrl)) {
    if (candidateKeys.has(protectedKey)) return true
  }

  return false
}

export function isCameraMotionGuideReferenceAsset(asset: CameraMotionGuideReferenceAsset | undefined | null): boolean {
  const motion = asset?.cameraMotion
  if (!motion) return false
  if (motion.nodeType === 'draft') return motion.status !== 'rendered'
  if (motion.status === 'rendered' || motion.outputVideoUrl) return false
  return asset?.operation === 'camera-motion' && Boolean(motion.guideVideoUrl || motion.userVisibleGuideVideoUrl)
}

export function isCameraMotionBackendGuideVideoReference(
  reference: CameraMotionGuideVideoReference | undefined | null,
  assets: ReadonlyMap<string, CameraMotionGuideReferenceAsset>,
): boolean {
  if (!reference) return false

  const referenceAsset = reference.videoAssetId ? assets.get(reference.videoAssetId) : undefined
  if (isCameraMotionGuideReferenceAsset(referenceAsset)) return true
  if (referenceAsset) return false

  if (!reference.videoUrl) return false

  for (const asset of assets.values()) {
    const backendGuideUrl = asset.cameraMotion?.guideVideoUrl
    if (backendGuideUrl && mediaUrlsMatch(reference.videoUrl, backendGuideUrl)) {
      return true
    }
  }

  return false
}

export function isCameraMotionGuideDraftAsset(asset: {
  cameraMotion?: {
    nodeType?: 'source' | 'control' | 'draft'
    status?: string
    userVisibleGuideVideoUrl?: string
    finalFrameUrl?: string
    finalFrameThumbnailUrl?: string
  } | null
  operation?: string | null
}): boolean {
  return (
    asset.operation === 'camera-motion' &&
    asset.cameraMotion?.nodeType === 'draft' &&
    asset.cameraMotion.status !== 'rendered' &&
    Boolean(asset.cameraMotion.userVisibleGuideVideoUrl)
  )
}

export function getCameraMotionGuideVideoUri(
  asset: Parameters<typeof isCameraMotionGuideDraftAsset>[0],
): string | undefined {
  return isCameraMotionGuideDraftAsset(asset) ? asset.cameraMotion?.userVisibleGuideVideoUrl : undefined
}

export function getCameraMotionFinalFrameUri(
  asset: Parameters<typeof isCameraMotionGuideDraftAsset>[0],
): string | undefined {
  return isCameraMotionGuideDraftAsset(asset)
    ? (asset.cameraMotion?.finalFrameThumbnailUrl ?? asset.cameraMotion?.finalFrameUrl)
    : undefined
}

/**
 * Gets the visual preview URI for an asset.
 * For video: returns thumbnailUri (frame capture)
 * For audio: returns waveformUri (waveform image)
 *
 * @param asset - The asset to get preview for
 * @returns The preview URI, or undefined if not available
 */
export function getAssetPreviewUri(asset: Asset): string | undefined {
  if (isAudioAsset(asset)) {
    return asset.waveformUri ?? asset.thumbnailUri
  }
  return asset.thumbnailUri
}

/**
 * Creates an asset with hierarchy and generation metadata from a completed job.
 * Combines the backend asset data with frontend hierarchy information and generation params.
 *
 * This is used when a generation job completes and we need to create the final asset
 * with all the contextual information that was stored in the job.
 *
 * @param serverAsset - The asset data from the backend (job.asset from API response)
 * @param localJob - The local job that contains hierarchy and generation params
 * @param transformProxyUri - Function to transform proxy URIs based on quality settings
 * @returns Complete asset with hierarchy and generation metadata
 */
export function createAssetFromCompletedJob(
  serverAsset: Asset,
  localJob: Pick<
    Job,
    | 'sceneId'
    | 'extensionSourceFrame'
    | 'startFrameSourcePosition'
    | 'endFrameSourcePosition'
    | 'operation'
    | 'parentId'
    | 'siblingIndex'
    | 'shotNumber'
    | 'variationNumber'
    | 'offshootNumber'
    | 'generationParams'
    | 'userId'
  >,
  transformProxyUri: (uri: string) => string,
): Asset {
  return {
    ...serverAsset,
    proxyUri: serverAsset.proxyUri ? transformProxyUri(serverAsset.proxyUri) : undefined,
    sceneId: localJob.sceneId,
    extensionSourceFrame: localJob.extensionSourceFrame,
    startFrameSourcePosition: localJob.startFrameSourcePosition,
    endFrameSourcePosition: localJob.endFrameSourcePosition,
    operation: localJob.operation,
    parentId: localJob.parentId,
    siblingIndex: localJob.siblingIndex,
    shotNumber: localJob.shotNumber,
    variationNumber: localJob.variationNumber,
    offshootNumber: localJob.offshootNumber,
    // Generation params for row grouping
    modelId: localJob.generationParams?.modelId,
    aspectRatio: localJob.generationParams?.aspectRatio,
    resolution: localJob.generationParams?.resolution,
    sourceVideoAssetId: serverAsset.sourceVideoAssetId ?? localJob.generationParams?.videoAssetId,
    // Inpaint ("Edit Region") link-back: a job that carried a mask was an
    // inpaint, so record the edited-from source on the result for the details
    // panel (mirrors the markup tool's markupSourceAssetId).
    inpaintSourceAssetId:
      localJob.generationParams?.maskUrl && localJob.generationParams?.sourceAssetId
        ? localJob.generationParams.sourceAssetId
        : undefined,
    // Author attribution
    userId: localJob.userId,
  }
}

/**
 * Processes a completed generation job: creates the asset, removes the job,
 * refreshes billing, preloads video, and checks first-generation milestone.
 *
 * This is the single canonical completion handler — called from both the
 * WebSocket handler and the polling reconciler in useWsMessageDispatcher.
 *
 * Race safety: callers must check `jobs.get(jobId)` before calling.
 * After the first call runs removeJob, subsequent lookups return undefined → skip.
 */
export function processCompletedJob(jobId: string, serverAsset: Asset, localJob: Job): void {
  const store = useStore.getState()

  // Defer if this job's node is currently being dragged on the canvas.
  // The completion will be flushed when the drag ends:
  //   - V2: the React Flow panel's handleNodesChange
  //   - V3: generationDragLifecycle via CanvasEngineView drag:end
  // This prevents the node identity change (job-{id} -> asset-{assetId}) from
  // resetting drag state while the gesture is still active.
  if (store.draggingCanvasJobIds.has(jobId)) {
    store.addDeferredJobCompletion(jobId, serverAsset, localJob)
    return
  }

  const assetWithHierarchy = createAssetFromCompletedJob(serverAsset, localJob, transformProxyUri)

  // addAssetFromJob wraps its Yjs writes in SYSTEM_ORIGIN.
  // Wrap removeJob in the same origin so the entire job→asset transition is non-undoable.
  // Attached nodes are projected by the server with an atomic ownership check.
  // Polls and delayed websocket completions must only retire their local job.
  if (!localJob.targetAssetId) addAssetFromJob({ ...localJob, status: 'completed', asset: assetWithHierarchy })
  yTransact(() => {
    useStore.getState().removeJob(jobId)
  }, SYSTEM_ORIGIN)

  window.dispatchEvent(new Event('project-billing-refresh'))

  // Warm the proxy for first playback. A video that completed ahead of its
  // proxies has none yet — its proxy is warmed by `generation.finalized`
  // instead, so the original is not pulled down in full only to be replaced
  // seconds later (hover-scrub still warms it on demand).
  const isVideo = assetWithHierarchy.mimeType?.startsWith('video/')
  if (isVideo && assetWithHierarchy.proxyUri) {
    queueFullFileVideoPreload(assetWithHierarchy.id, getStorageUrl(assetWithHierarchy.proxyUri), 'generation-complete')
  }

  useSubmissionProcessorStore.getState().checkFirstGenerationCompleted(jobId)
}

export function isFileAsset(asset: AssetMediaKindInput): boolean {
  return getAssetMediaKind(asset) === 'file'
}
